'use client'

import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { getUserProfile, User } from '@/lib/crate-core'
import { clearCredentials, getCredentials, setCredentials } from '@/lib/utils'
import { getBugsnagClient } from '@/lib/bugsnag/bugsnag'
import { login as apiLogin } from '@/lib/auth/auth'
import { toast } from 'sonner'

// create context
const UserContext = createContext<{
  user?: User
  userToken?: string
  isAuthenticating: boolean
  login: (identifier: string, password: string) => void
  logout: () => void
  hasProFeature: (featureName: string) => boolean
}>({
  user: undefined,
  userToken: undefined,
  isAuthenticating: false,
  login: () => {},
  logout: () => {},
  hasProFeature: () => false,
})

const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | undefined>(undefined)
  const [userToken, setUserToken] = useState<string | undefined>(undefined)
  const [isAuthenticating, setIsAuthenticating] = useState(true)

  const login = useCallback((identifier: string, password: string) => {
    console.debug('Attempting to login with identifier:', identifier)

    setIsAuthenticating(true)

    apiLogin({ identifier, password })
      .then(({ accessToken, refreshToken }) => {
        console.debug('Login successful, access token:', accessToken)

        // Update user token state
        setUserToken(accessToken)

        // Set credentials in local storage or cookies
        setCredentials(accessToken, refreshToken)

        // Optionally, you can fetch the user profile after login
        return getUserProfile()
      })
      .then((userProfile) => {
        if (userProfile) {
          console.debug('User profile fetched:', userProfile)
          setUser(userProfile)

          // Set Bugsnag user context
          getBugsnagClient()?.setUser(`${userProfile.id}`, userProfile.email, userProfile.username)
        } else {
          console.warn('No user profile found after login')
        }
      })
      .catch((error) => {
        console.error('Login failed:', error)
        clearCredentials()
        toast.error('Login failed. Please check your credentials and try again.')
      })
      .finally(() => {
        console.debug('Authentication process completed')
        setIsAuthenticating(false)
      })
  }, [])

  const logout = useCallback(() => {
    console.debug('Logging out user')
    clearCredentials()
  }, [])

  useEffect(() => {
    console.debug('Getting user profile...')

    const abortController = new AbortController()

    const fn = async () => {
      // If we have no credentials, abort
      const { accessToken } = getCredentials()

      if (!accessToken) {
        console.debug('No credentials found!')
        return null
      }

      return await getUserProfile({ signal: abortController.signal })
    }

    setIsAuthenticating(true)

    fn()
      .then((r) => {
        if (!r) {
          console.debug('No user found!')
          return null
        }

        console.debug('Welcome, %s!', r.email)
        // assign a random cohort to user based on id even/odd
        r.cohort = r.id % 2 === 0 ? 'treatment' : 'control'
        // console.log('User cohort:', r.cohort)
        // Always update user token state
        const { accessToken } = getCredentials()
        setUserToken(accessToken)
        setUser(r)

        try {
          getBugsnagClient()?.setUser(`${r.id}`, r.email, r.username)
        } catch (e) {
          console.error('Error setting Bugsnag user', e)
        }
      })
      .catch((e) => {
        console.error('Error getting user profile', e)

        // Handle unauthenticated user
        if (e.response?.status === 401) {
          console.debug('User is not authenticated, clearing credentials')
          clearCredentials()
        }
      })
      .finally(() => setIsAuthenticating(false))

    return () => {
      abortController.abort('Component unmounted')
    }
  }, [])

  /**
   * Check if the user has a specific pro feature.
   * @param featureName The name of the feature to check
   * @returns boolean
   */
  const hasProFeature = useCallback(
    (featureName: string): boolean => {
      if (!user?.team?.subscription?.plan?.features) return false

      const features = user.team.subscription.plan.features.filter((f) => f.name === featureName)

      if (!features.length) return false

      // Find a feature that has type 'level' and statement contains '1' (meaning at least pro)
      const levelFeature = features.find((f) =>
        f.policy?.statements.find((s) => s.type === 'level' && s.statement.includes('1'))
      )

      return !!levelFeature
    },
    [user]
  )

  return (
    // the Provider gives access to the context to its children
    <UserContext.Provider
      value={{
        user,
        userToken,
        isAuthenticating,
        login,
        logout,
        hasProFeature,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

const useUserContext = () => useContext(UserContext)

export { useUserContext, UserContextProvider }
